import { Button } from "@bowfast/ui/components/button";
import { Card, CardContent } from "@bowfast/ui/components/card";
import { cn } from "@bowfast/ui/lib/utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import {
  AnchorIcon,
  CheckCircle2Icon,
  CompassIcon,
  PlusIcon,
  SailboatIcon,
  SearchIcon,
  XCircleIcon,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { ExpeditionCard } from "~/components/expedition-card";
import { ListLoadError } from "~/components/shared/ListLoadError";
import { ListSkeleton } from "~/components/shared/ListSkeleton";
import { fadeUp, stagger } from "~/lib/animations";
import { createSdk } from "~/lib/gql";
import { useOrg } from "~/lib/org-context";
import { LIST_TAB_LABELS } from "./expeditions.config";
import type { ExpeditionListItem, ListTabFilter } from "./expeditions.types";
import { LIST_TAB_FILTERS } from "./expeditions.types";
import { STATUS_TO_TAB, toCardProps } from "./expeditions.utils";

// ── KPI strip ─────────────────────────────────────────────────────────────────

function KpiStrip({ expeditions }: Readonly<{ expeditions: ExpeditionListItem[] }>) {
  const active = expeditions.filter((e) => e.status === "ACTIVE").length;
  const open = expeditions.filter((e) => e.status === "OPEN" || e.status === "FULLY_BOOKED").length;
  const planning = expeditions.filter((e) => e.status === "PLANNING").length;
  const completed = expeditions.filter((e) => e.status === "COMPLETED").length;

  const items = [
    { label: "Total", value: expeditions.length, Icon: AnchorIcon, color: "text-abyss-500" },
    { label: "Active", value: active, Icon: CompassIcon, color: "text-shallow-600" },
    { label: "Open", value: open, Icon: SailboatIcon, color: "text-abyss-400" },
    { label: "Planning", value: planning, Icon: CompassIcon, color: "text-muted-foreground" },
    { label: "Completed", value: completed, Icon: CheckCircle2Icon, color: "text-violet-500" },
  ];

  return (
    <div className="flex flex-wrap gap-2">
      {items.map((m) => (
        <div key={m.label} className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2">
          <m.Icon className={cn("size-3.5", m.color)} />
          <span className="font-heading text-sm font-bold tabular-nums">{m.value}</span>
          <span className="text-xs text-muted-foreground">{m.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function ExpeditionsListPage() {
  const { user } = useAuth();
  const token = user?.access_token ?? "";
  const { orgId } = useOrg();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<ListTabFilter>("all");
  const [search, setSearch] = useState("");

  const { data, isPending, isError } = useQuery({
    queryKey: ["expeditions", orgId],
    queryFn: () => createSdk(token).ExpeditionsList({ orgId }),
    enabled: !!orgId && !!token,
  });

  const expeditions = data?.expeditions ?? [];
  const activeCount = expeditions.filter((e) => e.status === "ACTIVE").length;

  const toggle = useCallback((tab: ListTabFilter) => setActiveTab(tab), []);

  const filtered = useMemo(() => {
    let list = expeditions;
    if (activeTab !== "all") list = list.filter((e) => STATUS_TO_TAB[e.status] === activeTab);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (e) =>
          e.name.toLowerCase().includes(q) ||
          (e.route?.name ?? "").toLowerCase().includes(q) ||
          (e.vessels[0]?.vessel.name ?? "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [expeditions, activeTab, search]);

  const tabCount = useCallback(
    (tab: ListTabFilter) =>
      tab === "all" ? expeditions.length : expeditions.filter((e) => STATUS_TO_TAB[e.status] === tab).length,
    [expeditions],
  );

  if (isPending) {
    return (
      <div className="mx-auto w-full max-w-350 space-y-5 p-4 md:p-6">
        <ListSkeleton rows={5} rowHeight="h-48" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mx-auto w-full max-w-350 p-4 md:p-6">
        <ListLoadError
          title="Couldn't load your expeditions"
          onRetry={() => queryClient.invalidateQueries({ queryKey: ["expeditions", orgId] })}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-350 space-y-5 p-4 md:p-6">
      {/* Page header */}
      <motion.div {...fadeUp} className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex size-11 items-center justify-center rounded-xl bg-abyss-50 dark:bg-abyss-800">
            <CompassIcon className="size-6 text-abyss-500" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold tracking-tight md:text-[1.375rem]">Expeditions</h1>
            <p className="text-sm text-muted-foreground">
              {expeditions.length} expedition{expeditions.length !== 1 ? "s" : ""} · {activeCount} at sea now
            </p>
          </div>
        </div>
        <Button size="sm" className="gap-2" nativeButton={false} render={<Link to="/expeditions/new" />}>
          <PlusIcon className="size-3.5" />
          New expedition
        </Button>
      </motion.div>

      {/* KPI strip */}
      <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.04 }}>
        <KpiStrip expeditions={expeditions} />
      </motion.div>

      {/* Active at-sea banner */}
      {activeCount > 0 && (
        <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.06 }}>
          <Card className="relative overflow-hidden border-0 bg-abyss-900 text-white">
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "radial-gradient(ellipse at 75% 50%, rgba(4,124,199,0.25) 0%, transparent 55%), radial-gradient(ellipse at 15% 80%, rgba(20,184,166,0.1) 0%, transparent 40%)",
              }}
              aria-hidden
            />
            <CardContent className="relative z-10 flex flex-wrap items-center gap-5 py-4">
              <div className="flex items-center gap-2">
                <span className="relative flex size-2.5">
                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-shallow-400 opacity-60" />
                  <span className="relative inline-flex size-2.5 rounded-full bg-shallow-400" />
                </span>
                <p className="font-heading text-sm font-semibold">
                  {activeCount} expedition{activeCount > 1 ? "s" : ""} at sea
                </p>
              </div>
              <p className="text-xs text-white/50">
                {expeditions
                  .filter((e) => e.status === "ACTIVE")
                  .map((e) => `${e.name} · ${e.route?.name ?? ""}`)
                  .join("  ·  ")}
              </p>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Toolbar: tabs + search */}
      <motion.div
        {...fadeUp}
        transition={{ ...fadeUp.transition, delay: 0.08 }}
        className="flex flex-wrap items-center gap-3"
      >
        {/* Tab strip */}
        <div className="flex gap-1 rounded-lg border border-border bg-card p-1">
          {LIST_TAB_FILTERS.map((tab) => {
            const count = tabCount(tab);
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => toggle(tab)}
                className={cn(
                  "relative flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                  isActive ? "text-white" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                {isActive && (
                  <motion.div
                    layoutId="exp-tab-bg"
                    className="absolute inset-0 rounded-md bg-abyss-500"
                    transition={{ type: "spring", stiffness: 500, damping: 35 }}
                  />
                )}
                <span className="relative z-10">{LIST_TAB_LABELS[tab]}</span>
                {count > 0 && (
                  <span
                    className={cn(
                      "relative z-10 rounded-full px-1.5 py-0.5 text-[0.625rem] font-bold tabular-nums",
                      isActive ? "bg-white/20 text-white" : "bg-muted text-muted-foreground",
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex-1" />

        {/* Search */}
        <div className="relative">
          <SearchIcon className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            placeholder="Search expeditions…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 w-52 rounded-lg border border-input bg-card pl-8 pr-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/20"
          />
        </div>
      </motion.div>

      {/* Grid */}
      <AnimatePresence mode="wait">
        {filtered.length > 0 ? (
          <motion.div
            key={`${activeTab}-${search}`}
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
            initial="initial"
            animate="animate"
            variants={stagger}
          >
            {filtered.map((exp) => (
              <motion.div key={exp.id} variants={fadeUp} className="h-full">
                <Link to="/expeditions/$expeditionSlug" params={{ expeditionSlug: exp.slug }} className="block h-full">
                  <ExpeditionCard {...toCardProps(exp)} className="h-full" />
                </Link>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div key="empty" {...fadeUp} className="flex flex-col items-center justify-center py-20 text-center">
            <div className="flex size-14 items-center justify-center rounded-2xl bg-muted">
              {search ? (
                <XCircleIcon className="size-7 text-muted-foreground/40" />
              ) : (
                <CompassIcon className="size-7 text-muted-foreground/40" />
              )}
            </div>
            <p className="mt-4 font-heading text-base font-semibold">
              {search ? "No expeditions match" : "No expeditions yet"}
            </p>
            <p className="mt-1 max-w-xs text-sm text-muted-foreground">
              {search ? "Try a different name, route, or vessel" : "Plan your first voyage and invite your crew"}
            </p>
            {!search && (
              <Button size="sm" className="mt-5 gap-2" nativeButton={false} render={<Link to="/expeditions/new" />}>
                <PlusIcon className="size-3.5" />
                New expedition
              </Button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
